import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { classNames } from "../../helpers";
import { userState } from "../../redux/reducers/userReducer";
import { markDashboardTourDone, shouldShowDashboardTour } from "../../utils/onboarding";

/** Spotlight tour over the club-owner sidebar. Targets are menu items marked with data-tour="<path>". */
const STEPS = [
  {
    title: "Your club is ready!",
    text: "Here's a quick 30-second tour of where everything lives. You can skip it anytime.",
  },
  {
    target: "/myclasses",
    title: "1. Create your classes",
    text: "Start here. Add a class with its name, price and capacity — for example “Yoga Flow”.",
  },
  {
    target: "/products",
    title: "2. Sell passes & subscriptions",
    text: "Create the credit passes and monthly subscriptions your members can buy online.",
  },
  {
    target: "/myclasses/myschedule",
    title: "3. Plan your timetable",
    text: "Pick days and times for your classes. Members book straight from this schedule.",
  },
  {
    target: "/mymembers",
    title: "4. Invite your members",
    text: "Share your club link or import a member list so they can start booking.",
  },
  {
    title: "Let's add your first class",
    text: "It takes about 3 minutes. The Setup guide at the top shows what is done and what is next.",
    final: true,
  },
];

const findTarget = (path) => {
  if (!path) return null;
  const nodes = Array.from(document.querySelectorAll(`[data-tour="${path}"]`));
  return nodes.find((n) => {
    const r = n.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }) || null;
};

export default function OnboardingTour({ userId }) {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [index, setIndex] = React.useState(0);
  const [rect, setRect] = React.useState(null);
  const userData = useSelector(userState).userData;
  const step4 = (userData?.onBoardingStep || 0) >= 4;

  // waits for the account data too (onBoardingStep), so a reload or another way onto the dashboard still shows it once
  React.useEffect(() => {
    if (!userId || open) return undefined;
    const timer = setTimeout(() => {
      if (shouldShowDashboardTour(userId, userData)) setOpen(true);
    }, 900);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, step4]);

  const step = STEPS[index];

  const measure = React.useCallback(() => {
    const node = findTarget(step?.target);
    setRect(node ? node.getBoundingClientRect() : null);
  }, [step]);

  React.useEffect(() => {
    if (!open) return undefined;
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, measure]);

  const close = React.useCallback(() => {
    markDashboardTourDone(userId);
    setOpen(false);
  }, [userId]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  if (!open || !step) return null;

  const pad = 6;
  const spotlight = rect
    ? { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }
    : null;
  const cardWidth = 340;
  const cardStyle = spotlight
    ? {
        top: Math.max(16, Math.min(spotlight.top - 8, window.innerHeight - 260)),
        left: Math.min(spotlight.left + spotlight.width + 18, window.innerWidth - cardWidth - 16),
        width: cardWidth,
      }
    : null;

  return (
    <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-labelledby="fc-tour-title">
      {spotlight ? (
        <div
          className="pointer-events-none fixed rounded-xl ring-2 ring-white transition-all duration-300"
          style={{ ...spotlight, boxShadow: "0 0 0 9999px rgba(29, 19, 32, 0.62)" }}
        />
      ) : (
        <div className="fixed inset-0 bg-[#1d1320]/60" />
      )}

      <div className={spotlight ? "" : "fixed inset-0 flex items-center justify-center p-4"}>
      <div
        className={classNames(
          "fc-pop rounded-3xl bg-white p-6 shadow-2xl",
          spotlight ? "fixed" : "relative w-full max-w-[420px]"
        )}
        style={cardStyle || undefined}
      >
        {spotlight && (
          <span
            className="absolute -left-2 top-6 h-4 w-4 rotate-45 bg-white"
            aria-hidden="true"
          />
        )}
        <div className="flex items-center justify-between">
          <span className="text-xs font-extrabold tracking-wide text-indigo-bz">
            {index === 0 ? "WELCOME" : step.final ? "YOU'RE ALL SET" : `STEP ${index} OF ${STEPS.length - 2}`}
          </span>
          <button type="button" onClick={close} className="text-sm font-bold text-gray-500 hover:text-gray-800" aria-label="Close tour">
            Skip tour
          </button>
        </div>
        <h2 id="fc-tour-title" className="mt-2 text-xl font-black text-gray-900">{step.title}</h2>
        <p className="mt-1.5 text-sm text-gray-600">{step.text}</p>
        {!spotlight && step.target && (
          <p className="mt-2 text-xs font-semibold text-gray-500">Find it in the menu (☰ at the top left).</p>
        )}

        <div className="mt-5 flex items-center justify-between gap-3">
          <div className="flex gap-1.5" aria-hidden="true">
            {STEPS.map((_, i) => (
              <span key={i} className={classNames("h-1.5 rounded-full transition-all", i === index ? "w-5 bg-indigo-bz" : "w-1.5 bg-gray-300")} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {index > 0 && !step.final && (
              <button type="button" onClick={() => setIndex((i) => i - 1)} className="rounded-xl px-3 py-2 text-sm font-bold text-gray-600 hover:bg-gray-100">
                Back
              </button>
            )}
            {step.final ? (
              <>
                <button type="button" onClick={close} className="rounded-xl px-3 py-2 text-sm font-bold text-gray-600 hover:bg-gray-100">
                  Later
                </button>
                <button
                  type="button"
                  onClick={() => {
                    close();
                    navigate("/myclasses/new");
                  }}
                  className="rounded-xl bg-brown-coffee px-4 py-2 text-sm font-extrabold text-white hover:bg-light-brown-coffee"
                >
                  Create my first class
                </button>
              </>
            ) : (
              <button type="button" onClick={() => setIndex((i) => i + 1)} className="rounded-xl bg-brown-coffee px-4 py-2 text-sm font-extrabold text-white hover:bg-light-brown-coffee">
                {index === 0 ? "Show me" : "Next"}
              </button>
            )}
          </div>
                </div>
      </div>
      </div>
    </div>
  );
}
